import api from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';

class RecipeService {
  constructor() {
    this.baseURL = API_ENDPOINTS.recipes;
  }

  // Get all recipes (with optional filters)
  async getRecipes(filters = {}) {
    try {
      const params = new URLSearchParams();
      const page = filters.page != null ? Number(filters.page) : 1;
      const limit = filters.limit != null ? Number(filters.limit) : 200;
      params.append('page', String(page));
      params.append('limit', String(limit));
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.category) params.append('category', filters.category);
      if (filters.tags) params.append('tags', filters.tags);
      if (filters.search) params.append('search', filters.search);
      if (filters.isPublic !== undefined) params.append('isPublic', filters.isPublic);
      if (filters.includeArchived) params.append('includeArchived', 'true');
      if (filters.archivedOnly) params.append('archivedOnly', 'true');

      const response = await api.get(`${this.baseURL}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recipes:', error);
      throw error;
    }
  }

  // Fetch every page of recipes (for admin/manager views with full catalog)
  async fetchAllRecipes(filters = {}) {
    const pageSize = 200;
    let page = 1;
    let allRows = [];
    let total = 0;

    while (true) {
      const data = await this.getRecipes({ ...filters, page, limit: pageSize });
      const batch = Array.isArray(data)
        ? data
        : data.recipeResult || data.recipes || [];

      allRows = allRows.concat(batch);
      total = data.pagination?.total ?? allRows.length;

      if (batch.length === 0 || allRows.length >= total) {
        break;
      }
      page += 1;
    }

    return {
      recipeResult: allRows,
      pagination: {
        total,
        page: 1,
        limit: allRows.length,
        pages: 1,
      },
    };
  }

  async getRecipeById(recipeId) {
    try {
      const response = await api.get(`${this.baseURL}/${recipeId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching recipe:', error);
      throw error;
    }
  }

  // Create a new recipe
  async createRecipe(recipeData) {
    try {
      const response = await api.post(this.baseURL, recipeData);
      return response.data;
    } catch (error) {
      console.error('Error creating recipe:', error);
      throw error;
    }
  }

  // PUT /api/recipes/:id — matches routes/recetas.js (mounted as /api/recipes in server.js)
  async updateRecipe(recipeId, recipeData) {
    try {
      const response = await api.put(`${this.baseURL}/${recipeId}`, recipeData);
      return response.data;
    } catch (error) {
      console.error('Error updating recipe:', error);
      throw error;
    }
  }

  // Delete a recipe
  async deleteRecipe(recipeId) {
    try {
      const response = await api.delete(`${this.baseURL}/${recipeId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting recipe:', error);
      throw error;
    }
  }

  // Duplicate/clone a recipe
  async duplicateRecipe(recipeId, newTitle = null) {
    try {
      const response = await api.post(`${this.baseURL}/${recipeId}/duplicate`, {
        newTitle
      });
      return response.data;
    } catch (error) {
      console.error('Error duplicating recipe:', error);
      throw error;
    }
  }

  // Archive or restore a recipe (soft-delete)
  async setRecipeArchived(recipeId, archived) {
    try {
      const response = await api.patch(`${this.baseURL}/${recipeId}/archive`, { archived });
      return response.data;
    } catch (error) {
      console.error('Error archiving recipe:', error);
      throw error;
    }
  }

  // Update recipe sharing permissions
  async updateRecipeSharing(recipeId, sharingSettings) {
    try {
      const response = await api.patch(`${this.baseURL}/${recipeId}/sharing`, sharingSettings);
      return response.data;
    } catch (error) {
      console.error('Error updating recipe sharing:', error);
      throw error;
    }
  }

  // Get user's recipes (backend list endpoint; no per-user route)
  async getUserRecipes(userId) {
    try {
      const response = await api.get(this.baseURL);
      return response.data;
    } catch (error) {
      console.error('Error fetching user recipes:', error);
      throw error;
    }
  }

  // Get public recipes
  async getPublicRecipes() {
    try {
      const response = await api.get(this.baseURL, { params: { isPublic: true } });
      return response.data;
    } catch (error) {
      console.error('Error fetching public recipes:', error);
      throw error;
    }
  }

  // Backend has no categories endpoint yet
  async getCategories() {
    return [];
  }

  // Backend has no popular-tags endpoint yet
  async getPopularTags() {
    return [];
  }

  // Search via list endpoint query params (no /search route)
  async searchRecipes(query, filters = {}) {
    try {
      return await this.getRecipes({ search: query, ...filters });
    } catch (error) {
      console.error('Error searching recipes:', error);
      throw error;
    }
  }

  // Parse plain text recipe using Claude AI
  async parseRecipeText(text) {
    try {
      const response = await api.post(API_ENDPOINTS.ai.parseText, { text });
      return response.data;
    } catch (error) {
      console.error('Error parsing recipe text with AI:', error);
      throw error;
    }
  }

  // Extract recipe from an image using Claude Vision AI
  async parseRecipeImage(imageFile) {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      const response = await api.post(API_ENDPOINTS.ai.parseImage, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      console.error('Error parsing recipe image with AI:', error);
      throw error;
    }
  }
}

const recipeService = new RecipeService();
export default recipeService; 